import { useQuery } from '@tanstack/react-query';
import { statusService, type StatusEntityType } from '@/services';

/**
 * Hook to fetch the default (first by seq_num) status for an entity type.
 * Used when creating new records to assign the initial status_id.
 */
export function useDefaultStatus(entityType: StatusEntityType) {
  return useQuery({
    queryKey: ['default-status', entityType],
    queryFn: async () => {
      const result = await statusService.getDefault(entityType, '');
      if (result.error) throw result.error;
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches the default status ID for an entity type.
 * Returns the ID of the first status record ordered by seq_num.
 */
export async function getDefaultStatusId(entityType: StatusEntityType): Promise<string | null> {
  const result = await statusService.getDefault(entityType, '');
  if (result.error) {
    console.error(`Failed to fetch default status for ${entityType}:`, result.error);
    return null;
  }
  return result.data?.id ?? null;
}

/**
 * Hook to fetch "active" statuses (for filtering active vendors/price books).
 * Looks for status with name containing "active" (case-insensitive).
 */
export function useActiveStatusId(entityType: StatusEntityType) {
  return useQuery({
    queryKey: ['active-status', entityType],
    queryFn: async () => {
      const result = await statusService.listByEntityType(entityType, '');
      if (result.error) throw result.error;
      const match = result.data.find((s) => s.name?.toLowerCase().includes('active'));
      return match ? { id: match.id, name: match.name } : null;
    },
    staleTime: 5 * 60 * 1000,
  });
}
